import { createColumnHelper } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import { useState } from 'react';

import { useEnvironment } from '@/react/portainer/environments/queries';
import { getDashboardRoute } from '@/react/portainer/environments/utils';
import { EnvironmentId } from '@/react/portainer/environments/types';

import { Link } from '@@/Link';

import { EnvironmentReconcileStatus } from '../types';

import { CorrectedCountButton, CorrectionModal } from './CorrectionModal';

type CorrectedResource = NonNullable<
  NonNullable<EnvironmentReconcileStatus['history']>[number]['resources']
>[number];

/** One correction, with the environment name resolved for searching and sorting. */
export type CorrectionRow = {
  id: string;
  environmentId: EnvironmentId;
  name: string;
  artifactName: string;
  /** When the environment corrected the drift, in Unix seconds. */
  correctedAt: number;
  revision?: string;
  resources: Array<CorrectedResource>;
};

const columnHelper = createColumnHelper<CorrectionRow>();

/**
 * Names the artifact a correction belongs to.
 *
 * Only shown when a workflow has more than one reconciled artifact, because
 * with one it says the same thing on every row.
 */
const artifactColumn = columnHelper.accessor('artifactName', {
  id: 'artifact',
  header: 'Artifact',
  meta: { width: '20%' },
  cell({ row: { original: correction } }) {
    return <span className="font-medium">{correction.artifactName}</span>;
  },
});

const columns = [
  columnHelper.accessor('name', {
    id: 'name',
    header: 'Environment',
    meta: { width: '34%' },
    cell({ row: { original: correction } }) {
      return <EnvironmentLink correction={correction} />;
    },
  }),

  columnHelper.accessor((row) => row.resources.length, {
    id: 'resources',
    header: 'Put back',
    meta: { width: '22%' },
    cell({ row: { original: correction, index } }) {
      return <ResourcesCell correction={correction} index={index} />;
    },
  }),

  columnHelper.accessor('revision', {
    id: 'revision',
    header: 'Revision',
    meta: { width: '14%' },
    cell({ row: { original: correction } }) {
      if (!correction.revision) {
        return <span>-</span>;
      }

      return (
        <span className="font-mono text-xs">
          {correction.revision.slice(0, 8)}
        </span>
      );
    },
  }),

  columnHelper.accessor('correctedAt', {
    id: 'correctedAt',
    header: 'Corrected',
    meta: { width: '30%' },
    cell({ row: { original: correction } }) {
      return <CorrectedAt seconds={correction.correctedAt} />;
    },
  }),
];

/**
 * The columns for one workflow's correction history.
 *
 * Widths are declared rather than left to the browser, for the same reason the
 * reconcile table declares them: the cells vary in length between rows, so an
 * auto-sized table moves its columns as the contents change.
 */
export function historyColumns(withArtifact: boolean) {
  if (!withArtifact) {
    return columns;
  }

  const widths = ['28%', '20%', '18%', '12%', '22%'];

  return [columns[0], artifactColumn, ...columns.slice(1)].map(
    (column, index) => ({ ...column, meta: { width: widths[index] } })
  );
}

/** The count of what was put back, which opens the patches when clicked. */
function ResourcesCell({
  correction,
  index,
}: {
  correction: CorrectionRow;
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);

  if (!correction.resources.length) {
    return <span>-</span>;
  }

  return (
    <>
      <CorrectedCountButton
        count={correction.resources.length}
        index={index}
        onClick={() => setIsOpen(true)}
      />

      {isOpen && (
        <CorrectionModal
          name={correction.name}
          resources={correction.resources}
          onDismiss={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Says how long ago, with the exact time on hover.
 *
 * The timestamp is the environment's own clock, because that is where the
 * correction happened, so an environment whose clock is wrong reads as wrong
 * here rather than being silently restated as the server's.
 */
function CorrectedAt({ seconds }: { seconds: number }) {
  if (!seconds) {
    return <span>-</span>;
  }

  const at = new Date(seconds * 1000);

  return (
    <time dateTime={at.toISOString()} title={at.toLocaleString()}>
      {formatDistanceToNow(at, { addSuffix: true })}
    </time>
  );
}

function EnvironmentLink({ correction }: { correction: CorrectionRow }) {
  const { data } = useEnvironment(correction.environmentId);

  if (!data) {
    return <span>{correction.name}</span>;
  }

  const { to, params } = getDashboardRoute(data);

  return (
    <Link
      to={to}
      params={params}
      data-cy={`workflow-correction-environment-${correction.environmentId}`}
    >
      {correction.name}
    </Link>
  );
}
